import { FC, lazy, Suspense, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Wellbore } from 'types/api-responses';
import {
  useAssignUnit,
  useRemoveWellbore,
  useSetPrimaryWellbore,
  type WellboreTarget,
} from '../hooks';
import { wellUnitCandidateKeys, wellWorkspaceKeys } from '../query-keys';
import { WellboreGeometryTearsheet } from 'pages/pre-drill-wellbore-workspace/wellbore-geometry-tearsheet';
import { WellboreGeometrySourceModal } from './wellbore-geometry-source-modal';
import { DestroyConfirmation } from 'components/destroy-confirmation';
import { AddWellboreTearsheet } from './wellbore/add-wellbore-tearsheet';
import {
  DataTableSkeleton,
  InlineNotification,
  SkeletonPlaceholder,
} from '@carbon/react';
import { ProvideRootResource } from 'components/hydrate-atoms';
import { MountWhenVisible } from 'components/mount-when-visible';
import { PermitsTable } from 'components/well-page-content/permits-table';
import { PerforationsTable } from 'components/well-page-content/perforations-table';
import { Stimulations } from 'components/well-page-content/stimulations';
import { WellboresTable } from 'components/well-page-content/wellbores-table';
import UnitDetails from 'components/well-page-content/well-unit-details-table';
import { WellMap } from 'components/well-page-content/well-map';
import { WellAnalysis } from 'components/well-page-content/well-analysis';
import { useWellAnalysisHrefs } from 'components/well-page-content/hooks';
import {
  WorkspaceCard,
  WorkspaceCardNote,
  type WellScope,
} from 'components/workspace-cards';
import type { TraceColors } from 'components/wells-color-by';
import { useWellCompletions } from 'lib/wells';
import type { ScopedWell } from './use-scoped-well';
import styles from './well-section-cards.module.scss';

// Same lazy split the well page uses: the Explorer runs a large hook pipeline and fires
// several requests on mount, so it stays off the workspace's critical path and only
// mounts when scrolled into view.
const SubsurfaceExplorer = lazy(() =>
  import('components/well-page-content/subsurface-explorer').then((m) => ({
    default: m.SubsurfaceExplorer,
  }))
);

/**
 * Sections held back from this release: their cards render a notice instead of the
 * surface. Flip an entry to false to ship it — the real body is still wired below and
 * runs unchanged, so nothing has to be rebuilt to turn one on.
 */
const HELD_BACK = {
  subsurfaceExplorer: true,
};

/**
 * What a held-back card shows in place of its surface.
 *
 * Named rather than hidden: the mock puts these sections in the workspace, and a
 * reader who knows the Well page has them would otherwise read the gap as a fault.
 * @param {{ section: string }} props The section's name, as its card titles it.
 * @return {JSX.Element} The notice.
 */
const ComingNextRelease: FC<{ section: string }> = ({ section }) => (
  <InlineNotification
    kind="info"
    lowContrast
    hideCloseButton
    className={styles.heldBackNotification}
    title="Coming in the next release"
    subtitle={`${section} is not available in a Well Workspace yet. It is on the well's own page in the meantime.`}
  />
);

interface SectionProps {
  uwi: string;
  /**
   * The staged row's id, when it has one. The address a well authored in this workspace is
   * reached by — it has no canonical twin and no meaningful uwi of its own.
   */
  stagedWellId: number | null;
  /**
   * True for a well authored here (Create New Well): no canonical `Well` stands behind it, so
   * the sections that read one have nothing to read and the workspace's own endpoints serve
   * them instead.
   */
  authored: boolean;
  /**
   * The bore the well's geography, units and promoted line derive from. Undefined for a
   * well not authored here (no bore block is served for one); null means authored but
   * boreless. Only the Wellbore card reads it.
   */
  primaryCompletionUwi14?: string | null;
  /**
   * The bores this well has drawn. Gates the primary radio and the remove action — a row
   * can carry a predrill_type and still be a real staged bore, so the list is the only
   * reliable discriminator. Only the Wellbore card reads it.
   */
  boreCompletionUwi14s?: string[];
  scoped: ScopedWell;
  scope: WellScope;
  /**
   * This workspace (INS-12130). Passed to every section that reads the wells query, so
   * a promoted workspace's rows resolve as-of promotion and a draft's show its staged
   * edits. The Well page omits it and reads live.
   */
  workspaceId: number;
}

// Every production section reads the canonical well's HATEOAS hrefs, so each card waits
// on it rather than rendering with an empty href. A failed read is reported, not
// skeletoned: without that branch one failed `/wells/:uwi` left every card loading.
const WaitingForWell: FC<{
  title: string;
  scope: WellScope;
  error?: boolean;
}> = ({ title, scope, error = false }) => (
  <WorkspaceCard title={title} scope={scope}>
    {error ? (
      <InlineNotification
        kind="error"
        title="Error"
        subtitle="Could not load this well."
        lowContrast
        hideCloseButton
      />
    ) : (
      <DataTableSkeleton
        showHeader={false}
        showToolbar={false}
        columnCount={6}
        rowCount={3}
      />
    )}
  </WorkspaceCard>
);

export const PermitDetailsCard: FC<SectionProps> = ({
  uwi,
  scoped,
  scope,
  workspaceId,
}) => {
  if (!scoped.well)
    return (
      <WaitingForWell
        error={scoped.error}
        title="Permit Details"
        scope={scope}
      />
    );

  return (
    <WorkspaceCard title="Permit Details" scope={scope}>
      <PermitsTable
        uwi={uwi}
        resourceHref={scoped.well.resources.queries.permitHeaders.href}
        workspaceId={workspaceId}
        // The geometry launchers stay on the Well page: a workspace inside a workspace
        // would take over the page the reader is working in.
      />
    </WorkspaceCard>
  );
};

/**
 * Unit Details.
 *
 * An existing well reads the canonical intersection analysis, as the Well page does. A well
 * AUTHORED here reads the workspace's own candidate endpoints instead — it has no canonical
 * twin for the analysis to run against — and gets the assignment control on top. Both answer
 * with the same envelope, so one table renders either.
 * @param {SectionProps} props The scoped well and its workspace.
 * @return {JSX.Element} The card.
 */
export const UnitDetailsCard: FC<SectionProps> = ({
  scoped,
  scope,
  workspaceId,
  stagedWellId,
  authored,
}) => {
  // The pick the radio column is showing. Null lets the standing assignment (else the
  // recommendation) seed it, so the card opens on the row the backend would choose.
  const [pickedUnitId, setPickedUnitId] = useState<string | null>(null);
  const assigner = useAssignUnit(workspaceId, stagedWellId);

  /* A unit the backend says is not a candidate must not stay picked. The Assign button
     disables only on "in flight", "nothing picked" and "already assigned" — a rejected
     unit is none of those, so leaving it selected leaves the identical doomed request one
     click away. Clearing hands the row back to the seed below, which re-reads from the
     list the refusal has just invalidated. Only for THIS refusal: a promoted workspace
     refuses every pick, and blanking the reader's choice would not help. */
  const rejectedPick = assigner.errorCode === 'UNIT_NOT_A_CANDIDATE';
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the server refused the pick, so the local pick has to clear; the verdict arrives from the mutation, not from this render.
    if (rejectedPick) setPickedUnitId(null);
  }, [rejectedPick]);

  /* Both come from useScopedWell, which resolves authored-vs-canonical once so this card,
     the Map and the Explorer read the same list. Branching here instead is what left the
     map looking at an empty canonical analysis while this table showed the real units. */
  const data = scoped.intersection;
  const loading = scoped.intersectionLoading;

  const units = data?.intersectingUnits ?? [];
  const seededPick =
    pickedUnitId ??
    units.find((unit) => unit.assignedToUnit)?.name ??
    units.find((unit) => unit.bestFit)?.name ??
    null;

  return (
    <WorkspaceCard title="Unit Details" scope={scope}>
      {assigner.error && (
        <InlineNotification
          kind="error"
          title="Could not assign the unit"
          subtitle={assigner.error}
          lowContrast
          onCloseButtonClick={() => assigner.reset()}
        />
      )}
      {loading ? (
        <DataTableSkeleton
          showHeader={false}
          showToolbar={false}
          columnCount={10}
          rowCount={3}
        />
      ) : units.length ? (
        <UnitDetails
          data={data!}
          selectedUnitIds={scoped.selectedUnitIds}
          onSelectionChange={scoped.onUnitSelectionChange}
          assignment={
            authored
              ? {
                  pickedUnitId: seededPick,
                  onPick: setPickedUnitId,
                  onAssign: () => seededPick && assigner.assign(seededPick),
                  assigning: assigner.saving,
                }
              : undefined
          }
        />
      ) : (
        <InlineNotification
          kind="warning"
          title={
            authored
              ? 'No units intersect this well yet — add a wellbore geometry first'
              : 'No units intersect with this well'
          }
          lowContrast
          hideCloseButton
        />
      )}
    </WorkspaceCard>
  );
};

export const WellboreCard: FC<SectionProps> = ({
  uwi,
  scoped,
  scope,
  workspaceId,
  stagedWellId,
  authored,
  primaryCompletionUwi14,
  boreCompletionUwi14s,
}) => {
  /* A well authored HERE is the only one whose bores this card can change: an ingested well
     arrives with its own, and a member well has no staged row to hang them on until it has
     one. Hoisted because every action below asks the same question. */
  const canAuthorBores = authored && stagedWellId !== null;
  /**
   * The same prop, or nothing. Every action below is offered on exactly the wells this card
   * can author, and the table reads `undefined` as "this host does not offer it at all".
   * @param {T} offered What to pass when the card can author bores.
   * @return {T | undefined} It, or undefined.
   */
  const whenAuthoring = <T,>(offered: T): T | undefined =>
    canAuthorBores ? offered : undefined;

  /* Which bore the build sheet is writing to, or null while it is closed. `create` adds
     another bore; `rebuild` replaces one in place. Offered only for a well authored here —
     an ingested well arrives with its bores and there is nothing for the reader to build. */
  const [buildTarget, setBuildTarget] = useState<WellboreTarget | null>(null);
  // Story 7: the row's edit action opens the geometry tearsheet on the bore alone; the
  // sheet's first read resolves (or creates) the geometry workspace.
  const [geometryUwi14, setGeometryUwi14] = useState<string | null>(null);
  // Remounts the table on close so a saved geometry shows its new source and type.
  const [reloadKey, setReloadKey] = useState(0);
  // A geometry edit or a source change is a staged change on the workspace: the
  // header's Promote reads `promotable` off the workspace, so it is re-read along
  // with the table, or the button stays as it was until the page is reloaded.
  const queryClient = useQueryClient();
  const refreshWorkspace = () => {
    setReloadKey((key) => key + 1);
    void queryClient.invalidateQueries({
      queryKey: wellWorkspaceKeys.detail(workspaceId),
    });
    /* A geometry edit moves the well, so which units it intersects moves with it — and a
       pick the new geometry no longer reaches is dropped server-side. Without this the
       Units card keeps showing that pick until the page is reloaded. */
    void queryClient.invalidateQueries({ queryKey: wellUnitCandidateKeys.all });
  };

  const handleEditGeometry = (row: Wellbore) => {
    setGeometryUwi14(row.uwi14);
  };
  // The row whose geometry source is being changed; the modal is open while set.
  const [sourceRow, setSourceRow] = useState<Wellbore | null>(null);
  // The row whose bore is being removed; the confirm is open while set.
  const [removeRow, setRemoveRow] = useState<Wellbore | null>(null);
  const remover = useRemoveWellbore(workspaceId, stagedWellId, uwi);
  const primary = useSetPrimaryWellbore(workspaceId, stagedWellId, uwi);
  /* A bore the backend says is not there is a bore the reader wanted gone: the end state
     they asked for already holds, so this closes and re-reads rather than reporting a
     failure. Every other refusal — a promoted workspace, a well that is not authored —
     describes something the removal did NOT do, and stays on the confirm to be read. */
  const goneAlready = remover.errorCode === 'BORE_NOT_FOUND';
  useEffect(() => {
    if (!goneAlready) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the bore is already gone, so the confirm closes itself; `goneAlready` comes from the mutation's error code.
    setRemoveRow(null);
    remover.reset();
    refreshWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `remover` and `refreshWorkspace` are rebuilt every render; the code is the trigger.
  }, [goneAlready]);
  /**
   * Advisories the backend raised while BUILDING the bore — an over-long derived tie, a
   * stick carrying no perforated length. The sheet closes on success, so without surfacing
   * them here they would be computed, sent, and never read.
   */
  /* Keyed BY BORE, not a single list. A well holds several bores now, and each save used
     to replace the whole list — so adding a second bore silently dropped the first one's
     advisories while its geometry was still standing. */
  const [buildWarnings, setBuildWarnings] = useState<Record<string, string[]>>(
    {}
  );
  /** A refusal the build sheet handed back because nothing in it could fix it. */
  const [buildFailure, setBuildFailure] = useState<string | null>(null);

  if (!scoped.well)
    return (
      <WaitingForWell error={scoped.error} title="Wellbores" scope={scope} />
    );

  /* No edit affordance in the card header. It used to carry one addressing the PRIMARY
     bore, added when the row pencil sat past a horizontal scroll and was easy to miss.
     Every row now carries its own (see `onEditGeometry` below), so a header pencil is a
     second control for a subset of what the rows already do — and an ambiguous one, since
     nothing about it says it edits the primary rather than the bore you are looking at. */
  return (
    <WorkspaceCard title="Wellbores" scope={scope}>
      {buildFailure && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Could not build this wellbore"
          subtitle={buildFailure}
          onCloseButtonClick={() => setBuildFailure(null)}
        />
      )}
      {/* The switch's own refusal. In the CARD, not the row: the radio reverts on the
          re-read, so without this the control would simply snap back with no reason
          given. */}
      {primary.error && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Could not change the primary wellbore"
          subtitle={primary.error}
          onCloseButtonClick={() => primary.reset()}
        />
      )}
      {Object.entries(buildWarnings).flatMap(([bore, warnings]) =>
        warnings.map((warning) => (
          <InlineNotification
            key={`${bore}:${warning}`}
            kind="warning"
            lowContrast
            title="Check this wellbore geometry"
            subtitle={warning}
            onCloseButtonClick={() =>
              setBuildWarnings((prev) => ({
                ...prev,
                [bore]: (prev[bore] ?? []).filter((w) => w !== warning),
              }))
            }
          />
        ))
      )}
      <WellboresTable
        key={reloadKey}
        uwi={uwi}
        wellboresHref={scoped.well.resources.queries.wellbores.href}
        initialPerforationFactor={
          scoped.well.estimatedPerforatedLateralLengthFactor ?? null
        }
        onEditGeometry={handleEditGeometry}
        onChangeGeometrySource={setSourceRow}
        onAddWellbore={whenAuthoring(() => setBuildTarget({ mode: 'create' }))}
        // Reopens the picker over an existing bore. Shown per row only for one the picker
        // built — the table applies that gate, not this.
        onRebuildWellbore={whenAuthoring((row) =>
          setBuildTarget({ mode: 'rebuild', completionUwi14: row.uwi14 })
        )}
        // Offered on any drawn bore, not only one the picker built — the table applies
        // only the pre-drill exclusion.
        onRemoveWellbore={whenAuthoring(setRemoveRow)}
        /* The marker and the switch are one radio column. Both are withheld together:
           the pointer is served only for an authored well, and a radio group with
           nothing selected would assert the well has no primary rather than that the
           page does not know. */
        primaryCompletionUwi14={whenAuthoring(primaryCompletionUwi14 ?? null)}
        onSetPrimary={whenAuthoring((row) => primary.setPrimary(row.uwi14))}
        drawnBoreUwi14s={boreCompletionUwi14s}
        pendingPrimaryUwi14={primary.pendingUwi14}
        settingPrimary={primary.setting}
        simplifiedGeometryBanner="workspace"
        expandableWellborePoints
        wellWorkspaceId={workspaceId}
      />
      <WellboreGeometryTearsheet
        uwi14={geometryUwi14}
        wellName={scoped.well.name}
        wellWorkspaceId={workspaceId}
        open={geometryUwi14 !== null}
        onClose={() => {
          setGeometryUwi14(null);
          refreshWorkspace();
        }}
      />
      <AddWellboreTearsheet
        open={buildTarget !== null}
        workspaceId={workspaceId}
        stagedWellId={stagedWellId}
        uwi={uwi}
        wellName={scoped.well?.name ?? ''}
        target={buildTarget ?? { mode: 'create' }}
        onClose={() => setBuildTarget(null)}
        // The bore changes the well's geographic attributes, its unit intersection and the
        // promote gate — all of which live outside this card, so the whole scope is re-read.
        // The build's own advisories outlive the sheet, which closes on success.
        onBuilt={(built) => {
          setBuildFailure(null);
          setBuildWarnings((prev) => ({
            ...prev,
            // Keyed by the bore the server says it wrote, which on a rebuild is the bore
            // that was targeted — so its old advisories are replaced rather than added to.
            [built.wellbore.completionUwi14]: built.warnings ?? [],
          }));
          refreshWorkspace();
        }}
        /* The sheet could not act on this one — the well or the bore is wrong, not the
           selection. Re-read regardless of what it was: if the bore has in fact gone, the
           row disappears and the action cannot be tried again, which is what keeps a
           refusal from being retried forever against something that no longer exists. */
        onFailed={(message) => {
          setBuildFailure(message);
          refreshWorkspace();
        }}
      />
      <WellboreGeometrySourceModal
        open={sourceRow !== null}
        wellWorkspaceId={workspaceId}
        uwi14={sourceRow?.uwi14 ?? null}
        wellName={scoped.well.name}
        onClose={() => setSourceRow(null)}
        // Remount the table so the row shows the source it now follows.
        onSaved={refreshWorkspace}
      />
      {/* The two consequences are stated CONDITIONALLY rather than branched on, because
          the card cannot tell which bore is primary or how many remain: nothing serves the
          primary pointer on a page load, and the bore count lives inside the table. Naming
          both with their conditions is honest; guessing at either would not be. */}
      <DestroyConfirmation
        open={removeRow !== null}
        size="sm"
        modalHeading="Remove this wellbore?"
        primaryButtonText="Remove wellbore"
        primaryButtonDisabled={remover.removing}
        preventCloseOnClickOutside={remover.removing}
        error={goneAlready ? undefined : (remover.error ?? undefined)}
        onRequestClose={() => {
          if (remover.removing) return;
          setRemoveRow(null);
          remover.reset();
        }}
        onRequestSubmit={() => {
          if (removeRow === null) return;
          remover.remove(removeRow.uwi14, () => {
            setRemoveRow(null);
            // The table is not React Query — only its host's remount repaints it.
            refreshWorkspace();
          });
        }}
      >
        <p>
          The trajectory for {removeRow?.uwi14} and the fields derived from it
          are removed immediately. You can’t undo this action.
        </p>
        <p>
          If this is the well’s primary wellbore, its unit assignment is cleared
          and State, County and Hole Direction are recomputed from the next one.
          If it is the last, the well can’t be promoted until a wellbore is
          added again.
        </p>
      </DestroyConfirmation>
    </WorkspaceCard>
  );
};

/**
 * The well's completions, the same table the Well page shows.
 *
 * Read live, unlike the sections around it: the perforations query takes no
 * workspace, so a draft shows no staged changes here and a promoted workspace
 * shows the well's current completions rather than those at promotion.
 * @param {SectionProps} props The card's well and stepper scope.
 * @return {JSX.Element} The card.
 */
export const WellboreCompletionsCard: FC<SectionProps> = ({
  uwi,
  scoped,
  scope,
}) => {
  // Read once the well has resolved, when the table below mounts.
  const { data: completions, error: completionsError } = useWellCompletions(
    uwi,
    scoped.well?.resources.queries.wellCompletions?.href ?? ''
  );

  if (!scoped.well)
    return (
      <WaitingForWell
        error={scoped.error}
        title="Wellbore Completions"
        scope={scope}
      />
    );

  return (
    <WorkspaceCard title="Wellbore Completions" scope={scope}>
      <PerforationsTable
        uwi={uwi}
        resourceHref={
          scoped.well.resources.queries.wellPerforations?.href ?? ''
        }
        completions={completions}
        completionsError={completionsError}
      />
    </WorkspaceCard>
  );
};

export const StimulationsCard: FC<SectionProps> = ({
  uwi,
  scope,
  workspaceId,
}) => (
  <WorkspaceCard title="Stimulations" scope={scope}>
    <Stimulations
      uwi={uwi}
      resourceHref="/queries/source_well_stimulations"
      workspaceId={workspaceId}
    />
  </WorkspaceCard>
);

export const WellMapCard: FC<SectionProps & { traceColors?: TraceColors }> = ({
  uwi,
  scoped,
  scope,
  workspaceId,
  traceColors,
}) => (
  <WorkspaceCard title="Map" scope={{ ...scope, prefix: 'scoped to' }}>
    <div className={styles.map}>
      {/* ProvideRootResource is what the map layers read their root hrefs from — the
          well page wraps the map the same way. */}
      <ProvideRootResource>
        {scoped.well ? (
          <WellMap
            uwi={uwi}
            intersectionData={scoped.intersection}
            selectedUnitIds={scoped.selectedUnitIds}
            workspaceId={workspaceId}
            traceColors={traceColors}
            refreshToken={scoped.updatedAt}
          />
        ) : (
          <SkeletonPlaceholder className={styles.mapSkeleton} />
        )}
      </ProvideRootResource>
    </div>
  </WorkspaceCard>
);

export const SubsurfaceExplorerCard: FC<SectionProps> = ({
  uwi,
  scoped,
  scope,
  workspaceId,
}) => (
  <WorkspaceCard title="Subsurface Explorer" scope={scope}>
    {HELD_BACK.subsurfaceExplorer ? (
      <ComingNextRelease section="The Subsurface Explorer" />
    ) : scoped.well ? (
      <MountWhenVisible>
        <Suspense
          fallback={<SkeletonPlaceholder className={styles.explorerSkeleton} />}
        >
          <SubsurfaceExplorer
            uwi={uwi}
            wellName={scoped.well.name}
            scopedTileUrls={scoped.well.scoped?.tileUrls}
            intersectionData={scoped.intersection}
            selectedUnitIds={scoped.selectedUnitIds}
            workspaceId={workspaceId}
            // The mock shows all four views at once rather than one tab at a
            // time: Map and 3D above, 2D Profile and Gun Barrel below.
            layout="quad"
            mapPanel={
              <ProvideRootResource>
                <WellMap
                  uwi={uwi}
                  intersectionData={scoped.intersection}
                  selectedUnitIds={scoped.selectedUnitIds}
                  workspaceId={workspaceId}
                  refreshToken={scoped.updatedAt}
                />
              </ProvideRootResource>
            }
          />
        </Suspense>
      </MountWhenVisible>
    ) : (
      <SkeletonPlaceholder className={styles.explorerSkeleton} />
    )}
  </WorkspaceCard>
);

export const ProductionDetailsCard: FC<SectionProps> = ({ scoped, scope }) => {
  const hrefs = useWellAnalysisHrefs(scoped.well);

  return (
    <WorkspaceCard title="Production Details" scope={scope}>
      {hrefs ? (
        // The mock's "Production Details" is the well page's Well Analysis section —
        // same version pickers, chart and Excel export, same ~1.8k px of content.
        <WellAnalysis
          uwi={hrefs.uwi}
          forecastVersionHref={hrefs.productionForecastsVersionsHref}
          recordVersionHref={hrefs.productionRecordsVersionsHref}
          forecastHref={hrefs.productionForecastsHref}
          recordHref={hrefs.productionRecordsHref}
          dailyRecordHref={hrefs.dailyProductionHref}
          dailyRecordVersionHref={hrefs.dailyProductionVersionsHref}
          productionDataSources={hrefs.productionDataSources}
        />
      ) : (
        <WorkspaceCardNote>Loading production data…</WorkspaceCardNote>
      )}
    </WorkspaceCard>
  );
};
